"use client";

import { useRef, useState } from "react";

import {
  OWNER_INQUIRY_MAX_IMAGES,
  validateOwnerInquiry,
  type OwnerInquiryImage,
} from "@/domain/owner-inquiry";
import { DOG_SALON_SNAPS_PRODUCT } from "@/domain/product";
import { deliverInquiryToCoco, deliveryNoticeJa } from "@/lib/deliver-inquiry";
import { appendInquiryImages, imageFilesFromDataTransfer } from "@/lib/inquiry-images";
import { saveOwnerInquiry } from "@/lib/owner-inquiry-store";

/**
 * Always-on field note — private on-device by default; share is opt-in.
 */
export function FieldNoteFab({ onOpenDesk }: { onOpenDesk?: () => void }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [images, setImages] = useState<OwnerInquiryImage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  async function onPickFiles(files: FileList | File[] | null) {
    if (!files || (Array.isArray(files) ? files.length === 0 : files.length === 0)) {
      return;
    }
    setError(null);
    try {
      const { images: next, error: warn } = await appendInquiryImages(images, files);
      setImages(next);
      if (warn) setError(warn);
    } catch (e) {
      setError(e instanceof Error ? e.message : "画像の追加に失敗しました。");
    }
  }

  async function onPasteImages(e: React.ClipboardEvent) {
    const files = imageFilesFromDataTransfer(e.clipboardData);
    if (files.length === 0) return;
    e.preventDefault();
    await onPickFiles(files);
  }

  async function persist(opts: { alsoDeliver: boolean }) {
    setError(null);
    setNotice(null);
    const invalid = validateOwnerInquiry({
      topic: "field",
      body,
      contact: "",
      images,
    });
    if (invalid) {
      setError(invalid);
      return;
    }
    setSaving(true);
    try {
      const saved = await saveOwnerInquiry({
        topic: "field",
        body,
        contact: "",
        images,
      });
      let result: "shared" | "mailto" | "local" = "local";
      if (opts.alsoDeliver) {
        result = await deliverInquiryToCoco({
          inquiry: saved,
          email: DOG_SALON_SNAPS_PRODUCT.ownerDeskEmail,
        });
      }
      setBody("");
      setImages([]);
      setNotice(
        deliveryNoticeJa(result, { wantedDeliver: opts.alsoDeliver }),
      );
      window.setTimeout(() => {
        setOpen(false);
        setNotice(null);
      }, 1200);
    } catch (e) {
      setError(e instanceof Error ? e.message : "保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="field-note-fab"
        aria-label="現場メモ"
        onClick={() => {
          setOpen(true);
          setError(null);
          setNotice(null);
        }}
      >
        現場メモ
      </button>
      {open ? (
        <div className="field-note-sheet" role="dialog" aria-modal="true" aria-label="現場メモ">
          <div className="field-note-card panel">
            <h2 className="panel-title">現場メモ</h2>
            <label className="field full">
              <span>メモ</span>
              <textarea
                className="diary-input"
                rows={4}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onPaste={(e) => void onPasteImages(e)}
                placeholder="例: 毛玉「中」が高い"
                autoFocus
              />
            </label>
            <div className="field full">
              <span>画像（任意・最大{OWNER_INQUIRY_MAX_IMAGES}枚）</span>
              <div className="attach-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => cameraRef.current?.click()}
                >
                  写真を撮る
                </button>
                <button type="button" className="btn" onClick={() => fileRef.current?.click()}>
                  画像を選ぶ
                </button>
                <input
                  ref={cameraRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  onChange={(e) => {
                    void onPickFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => {
                    void onPickFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
              </div>
              {images.length > 0 ? (
                <ul className="attach-thumbs">
                  {images.map((img) => (
                    <li key={img.id}>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={img.dataUrl} alt="" />
                      <button
                        type="button"
                        className="attach-remove"
                        aria-label="画像を外す"
                        onClick={() =>
                          setImages((prev) => prev.filter((x) => x.id !== img.id))
                        }
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            {error ? <p className="error">{error}</p> : null}
            {notice ? <p className="notice">{notice}</p> : null}
            <div className="detail-actions">
              <button type="button" className="btn" onClick={() => setOpen(false)}>
                閉じる
              </button>
              {onOpenDesk ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setOpen(false);
                    onOpenDesk();
                  }}
                >
                  窓口を開く
                </button>
              ) : null}
              <button
                type="button"
                className="btn btn-primary"
                disabled={saving}
                onClick={() => void persist({ alsoDeliver: false })}
              >
                {saving ? "保存中…" : "この端末に残す"}
              </button>
              <button
                type="button"
                className="btn"
                disabled={saving}
                onClick={() => void persist({ alsoDeliver: true })}
              >
                共有／送る
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
